/**
 * Главный стор приложения на Svelte 5 runes.
 *
 * Единый источник правды: project, activeTool, selection, toolSettings, history.
 * Все мутации — через actions. Каждое значимое изменение пушит snapshot в историю
 * для undo/redo.
 */
import { uid, createProject, createShape } from '../domain/defaults';
import {
  nextNumberValue,
  renumberAll,
  withNumber,
  findNumberCollision,
  getNumberValue,
} from '../domain/numbering';
import { translateShape } from '../domain/geometry';
import type {
  Project,
  ProjectImage,
  Shape,
  Snapshot,
  ToolDefaults,
  ToolId,
} from '../domain/types';
import { saveProject, loadLastProject } from './persistence';

const MAX_HISTORY = 100;

/**
 * Глубокая копия массива shapes для истории (undo/redo).
 *
 * ВАЖНО: объекты хранятся в $state, поэтому обёрнуты в Proxy Svelte. Прямой
 * structuredClone(Proxy) падает с DataCloneError — отсюда зависал весь UI при
 * попытке начать вторую аннотацию (commit() выбрасывал исключение из mousedown).
 * $state.snapshot — rune Svelte 5, снимает прокси и возвращает глубокую «чистую»
 * копию.
 */
function snapshot(shapes: Shape[], selection: string[], background: any): any {
  return {
    shapes: $state.snapshot(shapes) as Shape[],
    selection: [...selection],
    background: $state.snapshot(background),
  };
}

class EditorStore {
  project = $state<Project>(createProject());
  activeTool = $state<ToolId>('select');
  selection = $state<string[]>([]);
  editingTextId = $state<string | null>(null);
  toolSettings = $state<Record<ToolId, Partial<ToolDefaults>>>({
    select: {},
    arrow: {},
    rect: {},
    pencil: {},
    text: {},
    number: {},
    blur: {},
  });
  get palette(): string[] {
    const colors = new Set<string>();
    for (const s of this.project.shapes) {
      if (s.type === 'arrow') {
        colors.add(s.strokeColor.toLowerCase());
      } else if (s.type === 'rect') {
        if (s.strokeEnabled || s.marker) colors.add(s.strokeColor.toLowerCase());
        if (s.fillEnabled && s.fillColor) colors.add(s.fillColor.toLowerCase());
      } else if (s.type === 'pencil') {
        if (s.strokeEnabled) colors.add(s.strokeColor.toLowerCase());
        if (s.closed && s.fillEnabled && s.fillColor) colors.add(s.fillColor.toLowerCase());
      } else if (s.type === 'number') {
        colors.add(s.strokeColor.toLowerCase());
        if (s.fillEnabled && s.fillColor) colors.add(s.fillColor.toLowerCase());
        if (s.textColor) colors.add(s.textColor.toLowerCase());
      } else if (s.type === 'text') {
        if (s.textColor) colors.add(s.textColor.toLowerCase());
        if (s.hasBackground && s.fillColor) colors.add(s.fillColor.toLowerCase());
      } else if (s.type === 'blur') {
        if (s.strokeEnabled) colors.add(s.strokeColor.toLowerCase());
      }
    }
    return Array.from(colors);
  }

  private past = $state<Snapshot[]>([]);
  private future = $state<Snapshot[]>([]);
  private autosaveTimer: ReturnType<typeof setTimeout> | null = null;
  /** Флаг: идёт программное восстановление — не плодим лишние snapshot-ы. */
  private restoring = false;

  // ─────────────────────────────────────────────────────────────────────────
  // Производные значения
  // ─────────────────────────────────────────────────────────────────────────
  get canUndo(): boolean {
    return this.past.length > 0;
  }
  get canRedo(): boolean {
    return this.future.length > 0;
  }

  selectedShapes = $derived.by(() => {
    const set = new Set(this.selection);
    return this.project.shapes.filter((s) => set.has(s.id));
  });

  /** Shape, отображаемый в панели свойств (первый выделенный). */
  primarySelection = $derived(this.selectedShapes[0]);

  /** Настройки для активного инструмента, слитые с базовыми дефолтами — для UI. */
  activeToolSettings = $derived(this.toolSettings[this.activeTool]);

  /** shapes отсортированы по z для отрисовки (меньше z = ниже). */
  shapesSorted = $derived([...this.project.shapes].sort((a, b) => a.z - b.z));

  // ─────────────────────────────────────────────────────────────────────────
  // История
  // ─────────────────────────────────────────────────────────────────────────
  /**
   * Зафиксировать текущее состояние в истории перед мутацией.
   * Публичный — компоненты вызывают его перед серией правок, чтобы все
   * промежуточные изменения (drag) свернулись в один undo-шаг через addShape
   * с commitHistory=false.
   */
  commit(): void {
    if (this.restoring) return;
    this.past.push(snapshot(this.project.shapes, this.selection, this.project.background));
    if (this.past.length > MAX_HISTORY) this.past.shift();
    this.future = [];
    this.touch();
  }

  discardLastCommit(): void {
    this.past.pop();
  }

  undo(): void {
    if (this.past.length === 0) return;
    const cur = snapshot(this.project.shapes, this.selection, this.project.background);
    const prev = this.past.pop()!;
    this.future.push(cur);
    this.restoring = true;
    this.project.shapes = prev.shapes;
    this.selection = prev.selection;
    if (prev.background) this.project.background = prev.background;
    this.restoring = false;
    this.touch();
  }

  redo(): void {
    if (this.future.length === 0) return;
    const cur = snapshot(this.project.shapes, this.selection, this.project.background);
    const next = this.future.pop()!;
    this.past.push(cur);
    this.restoring = true;
    this.project.shapes = next.shapes;
    this.selection = next.selection;
    if (next.background) this.project.background = next.background;
    this.restoring = false;
    this.touch();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Автосохранение
  // ─────────────────────────────────────────────────────────────────────────
  private touch(): void {
    this.project.updatedAt = Date.now();
    if (this.autosaveTimer) clearTimeout(this.autosaveTimer);
    this.autosaveTimer = setTimeout(() => {
      // $state.snapshot снимает Proxy — иначе IDB падает с DataCloneError.
      void saveProject($state.snapshot(this.project) as Project);
    }, 500);
  }

  async restoreLast(): Promise<void> {
    const last = await loadLastProject();
    if (!last) return;
    this.restoring = true;
    
    const defaultBg = {
      enabled: false,
      color: '#ffffff',
      padding: 50,
      cornerRadius: 12,
      shadowEnabled: true,
    };
    if (!last.background || typeof last.background !== 'object') {
      last.background = defaultBg;
    } else {
      last.background = { ...defaultBg, ...last.background };
    }
    
    this.project = last;
    this.selection = [];
    this.past = [];
    this.future = [];
    this.restoring = false;
  }

  async replaceProject(project: Project): Promise<void> {
    this.restoring = true;
    
    const defaultBg = {
      enabled: false,
      color: '#ffffff',
      padding: 50,
      cornerRadius: 12,
      shadowEnabled: true,
    };
    if (!project.background || typeof project.background !== 'object') {
      project.background = defaultBg;
    } else {
      project.background = { ...defaultBg, ...project.background };
    }

    this.project = project;
    this.selection = [];
    this.past = [];
    this.future = [];
    this.restoring = false;
    this.touch();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Инструменты и выделение
  // ─────────────────────────────────────────────────────────────────────────
  setTool(tool: ToolId): void {
    this.activeTool = tool;
    if (tool !== 'select') this.selection = [];
  }

  setToolSetting<K extends keyof ToolDefaults>(tool: ToolId, key: K, value: ToolDefaults[K]): void {
    this.toolSettings[tool] = { ...this.toolSettings[tool], [key]: value };
  }

  selectShape(id: string, additive = false): void {
    if (additive) {
      if (this.selection.includes(id)) {
        this.selection = this.selection.filter((s) => s !== id);
      } else {
        this.selection = [...this.selection, id];
      }
    } else {
      this.selection = [id];
    }
  }

  clearSelection(): void {
    this.selection = [];
  }

  updateBackground(patch: Partial<import('../domain/types').ProjectBackground>): void {
    this.commit();
    this.project.background = { ...this.project.background, ...patch };
    this.touch();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Мутации shapes
  // ─────────────────────────────────────────────────────────────────────────
  /** Найти shape по id. */
  getShape(id: string): Shape | undefined {
    return this.project.shapes.find((s) => s.id === id);
  }

  /** Следующий свободный z (для новых shape — поверх всех). */
  private nextZ(): number {
    return this.project.shapes.reduce((m, s) => Math.max(m, s.z), -1) + 1;
  }

  /**
   * Добавить shape в проект.
   * @param commitHistory — true для пользовательских действий (undo-able).
   */
  addShape(shape: Shape, commitHistory = true): void {
    if (commitHistory) this.commit();
    this.project.shapes = [...this.project.shapes, shape];
    this.touch();
  }

  /**
   * Заменить shape по id на новый. Иммутабельно для реактивности.
   */
  updateShape(id: string, patch: Partial<Shape>, commitHistory = false): void {
    const idx = this.project.shapes.findIndex((s) => s.id === id);
    if (idx === -1) return;
    if (commitHistory) this.commit();
    const cur = this.project.shapes[idx];
    const nextShape = { ...cur, ...patch } as Shape;

    if (cur.type === 'number') {
      const numberPatch = patch as Partial<import('../domain/types').NumberShape>;
      if (cur.globalMode || numberPatch.globalMode) {
        const GLOBAL_NUMBER_PROPERTIES = [
          'radius', 'arrowLength', 'hasArrow', 'textColor',
          'strokeColor', 'strokeWidth', 'strokeEnabled', 'fillColor', 'fillEnabled'
        ] as const;

        const isActivatingGlobal = numberPatch.globalMode === true && !cur.globalMode;
        const globalPatch: any = {};

        for (const prop of GLOBAL_NUMBER_PROPERTIES) {
          if (isActivatingGlobal) {
            globalPatch[prop] = (nextShape as any)[prop];
          } else if (prop in numberPatch) {
            globalPatch[prop] = numberPatch[prop];
          }
        }

        if (Object.keys(globalPatch).length > 0) {
          this.project.shapes = this.project.shapes.map((s) => {
            if (s.type === 'number') {
              return { ...s, ...globalPatch } as Shape;
            }
            return s;
          });
          this.toolSettings.number = { ...this.toolSettings.number, ...globalPatch };
        }
      }
    }

    if (cur.type === 'arrow') {
      const arrowPatch = patch as Partial<import('../domain/types').ArrowShape>;
      if (cur.globalMode || arrowPatch.globalMode) {
        const GLOBAL_ARROW_PROPERTIES = [
          'headSize', 'strokeColor', 'strokeWidth', 'dash', 'marker'
        ] as const;

        const isActivatingGlobal = arrowPatch.globalMode === true && !cur.globalMode;
        const globalPatch: any = {};

        for (const prop of GLOBAL_ARROW_PROPERTIES) {
          if (isActivatingGlobal) {
            globalPatch[prop] = (nextShape as any)[prop];
          } else if (prop in arrowPatch) {
            globalPatch[prop] = arrowPatch[prop];
          }
        }

        if (Object.keys(globalPatch).length > 0) {
          this.project.shapes = this.project.shapes.map((s) => {
            if (s.type === 'arrow') {
              return { ...s, ...globalPatch } as Shape;
            }
            return s;
          });
          this.toolSettings.arrow = { ...this.toolSettings.arrow, ...globalPatch };
        }
      }
    }

    this.project.shapes = [
      ...this.project.shapes.slice(0, idx),
      nextShape,
      ...this.project.shapes.slice(idx + 1),
    ];
    this.touch();
  }

  /** Полностью заменить shape (для сложных правок). */
  replaceShape(shape: Shape, commitHistory = false): void {
    const idx = this.project.shapes.findIndex((s) => s.id === shape.id);
    if (idx === -1) return;
    if (commitHistory) this.commit();
    this.project.shapes = [
      ...this.project.shapes.slice(0, idx),
      shape,
      ...this.project.shapes.slice(idx + 1),
    ];
    this.touch();
  }

  deleteShape(id: string, commitHistory = true): void {
    const existed = this.project.shapes.some((s) => s.id === id);
    if (!existed) return;
    if (commitHistory) this.commit();
    let next = this.project.shapes.filter((s) => s.id !== id);
    // Перенумеровать оставшиеся маркеры, чтобы последовательность не рвалась.
    next = renumberAll(next);
    this.project.shapes = next;
    this.selection = this.selection.filter((s) => s !== id);
    this.touch();
  }

  deleteSelection(): void {
    if (this.selection.length === 0) return;
    const toRemove = new Set(this.selection);
    this.commit();
    let next = this.project.shapes.filter((s) => !toRemove.has(s.id));
    next = renumberAll(next);
    this.project.shapes = next;
    this.selection = [];
    this.touch();
  }

  /** Переместить shape по вектору (dx, dy) в координатах изображения. */
  translateShape(id: string, dx: number, dy: number): void {
    const idx = this.project.shapes.findIndex((s) => s.id === id);
    if (idx === -1) return;
    const cur = this.project.shapes[idx];
    this.project.shapes = [
      ...this.project.shapes.slice(0, idx),
      translateShape(cur, dx, dy),
      ...this.project.shapes.slice(idx + 1),
    ];
    this.touch();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Z-порядок слоёв
  // ─────────────────────────────────────────────────────────────────────────
  reorderLayer(id: string, direction: 'up' | 'down' | 'top' | 'bottom'): void {
    this.commit();
    const sorted = [...this.project.shapes].sort((a, b) => a.z - b.z);
    const idx = sorted.findIndex((s) => s.id === id);
    if (idx === -1) return;
    const [item] = sorted.splice(idx, 1);
    if (direction === 'up') sorted.splice(Math.min(idx + 1, sorted.length), 0, item);
    else if (direction === 'down') sorted.splice(Math.max(idx - 1, 0), 0, item);
    else if (direction === 'top') sorted.push(item);
    else sorted.unshift(item);
    sorted.forEach((s, i) => (s.z = i));
    this.project.shapes = sorted;
    this.touch();
  }

  toggleVisible(id: string): void {
    const s = this.getShape(id);
    if (!s) return;
    this.updateShape(id, { visible: !s.visible });
  }

  toggleLocked(id: string): void {
    const s = this.getShape(id);
    if (!s) return;
    this.updateShape(id, { locked: !s.locked });
  }

  duplicateShape(id: string): void {
    const s = this.getShape(id);
    if (!s) return;
    this.commit();
    // $state.snapshot снимает Proxy с $state-объекта (structuredClone тут падает).
    const copy: Shape = {
      ...($state.snapshot(s) as Shape),
      id: uid('shape'),
      z: this.nextZ(),
      createdAt: Date.now(),
    };
    // чуть сдвинуть для визуального отделения
    this.project.shapes = [...this.project.shapes, translateShape(copy, 16, 16)];
    this.selection = [copy.id];
    this.touch();
  }

  updatePaletteColor(index: number, newColor: string): void {
    const oldColor = this.palette[index];
    if (oldColor.toLowerCase() === newColor.toLowerCase()) return;
    this.commit();
    
    this.palette[index] = newColor;

    const replaceColor = (c: string) => (c.toLowerCase() === oldColor.toLowerCase() ? newColor : c);

    this.project.shapes = this.project.shapes.map((s) => {
      const updated = { ...s };
      if ('strokeColor' in updated) updated.strokeColor = replaceColor(updated.strokeColor);
      if ('fillColor' in updated) updated.fillColor = replaceColor(updated.fillColor);
      if ('textColor' in updated) updated.textColor = replaceColor(updated.textColor);
      return updated as Shape;
    });

    for (const tool of Object.keys(this.toolSettings) as ToolId[]) {
      const settings = this.toolSettings[tool];
      if (settings.strokeColor) settings.strokeColor = replaceColor(settings.strokeColor);
      if (settings.fillColor) settings.fillColor = replaceColor(settings.fillColor);
      if (settings.textColor) settings.textColor = replaceColor(settings.textColor);
    }
    
    this.touch();
  }

  toggleVisibleSelection(): void {
    if (this.selection.length === 0) return;
    this.commit();
    const selected = this.project.shapes.filter((s) => this.selection.includes(s.id));
    const anyVisible = selected.some((s) => s.visible);
    this.project.shapes = this.project.shapes.map((s) => {
      if (this.selection.includes(s.id)) {
        return { ...s, visible: !anyVisible };
      }
      return s;
    });
    this.touch();
  }

  toggleLockedSelection(): void {
    if (this.selection.length === 0) return;
    this.commit();
    const selected = this.project.shapes.filter((s) => this.selection.includes(s.id));
    const anyUnlocked = selected.some((s) => !s.locked);
    this.project.shapes = this.project.shapes.map((s) => {
      if (this.selection.includes(s.id)) {
        return { ...s, locked: anyUnlocked };
      }
      return s;
    });
    this.touch();
  }

  duplicateSelection(): void {
    if (this.selection.length === 0) return;
    this.commit();
    const newShapes: Shape[] = [];
    const newSelection: string[] = [];
    let currentZ = this.nextZ();
    
    for (const id of this.selection) {
      const s = this.getShape(id);
      if (!s) continue;
      const copy: Shape = {
        ...($state.snapshot(s) as Shape),
        id: uid('shape'),
        z: currentZ++,
        createdAt: Date.now(),
      };
      const shifted = translateShape(copy, 16, 16);
      newShapes.push(shifted);
      newSelection.push(shifted.id);
    }
    this.project.shapes = [...this.project.shapes, ...newShapes];
    this.selection = newSelection;
    this.touch();
  }

  clearAllShapes(): void {
    if (this.project.shapes.length === 0) return;
    this.commit();
    this.project.shapes = [];
    this.selection = [];
    this.touch();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Изображение и вьюпорт
  // ─────────────────────────────────────────────────────────────────────────
  setImage(image: ProjectImage): void {
    this.commit();
    this.project.image = image;
    this.project.name = this.project.name || 'Скриншот';
    this.touch();
  }

  clearImage(): void {
    this.commit();
    this.project.image = null;
    this.project.shapes = [];
    this.selection = [];
    this.touch();
  }

  setViewport(scale: number, tx: number, ty: number): void {
    this.project.viewport = { scale, tx, ty };
    // вьюпорт не пушим в историю и не триггерим autosave (слишком часто)
  }

  zoomToFit(containerW: number, containerH: number): void {
    const img = this.project.image;
    if (!img) return;
    const scale = Math.min(containerW / img.width, containerH / img.height, 1);
    const tx = (containerW - img.width * scale) / 2;
    const ty = (containerH - img.height * scale) / 2;
    this.setViewport(scale, tx, ty);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Специфичные для инструментов операции
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Создать новый маркер-номер со стрелкой и автонумерацией.
   * Возвращает id созданного shape.
   */
  createNumberMarker(anchor: { x: number; y: number }, target: { x: number; y: number }): string {
    this.commit();
    const value = nextNumberValue(this.project.shapes);
    const z = this.nextZ();
    const settings = this.toolSettings.number;
    const angle =
      (Math.atan2(target.y - anchor.y, target.x - anchor.x) * 180) / Math.PI;
    const shape = createShape(
      'number',
      {
        x: anchor.x,
        y: anchor.y,
        value,
        arrowTarget: { ...target },
        arrowMode: 'target' as const,
        hasArrow: true,
        arrowAngle: angle,
      },
      z,
      settings,
    ) as Shape;
    this.project.shapes = [...this.project.shapes, shape];
    this.selection = [shape.id];
    this.touch();
    return shape.id;
  }

  /**
   * Изменить номер у маркера и при необходимости перенумеровать остальные,
   * чтобы последовательность оставалась непрерывной. Если указанное значение
   * занято другим маркером — остальные перенумеруются по z-порядку.
   */
  setNumberValue(id: string, value: number, renumberOthers = true): void {
    const idx = this.project.shapes.findIndex((s) => s.id === id);
    if (idx === -1) return;
    this.commit();
    let shapes = this.project.shapes.slice();
    shapes[idx] = withNumber(shapes[idx], value);
    if (renumberOthers) {
      // Перенумеруем остальные маркеры, пропуская текущий (его значение фиксировано).
      const others = shapes
        .filter((s) => s.id !== id)
        .filter((s) => getNumberValue(s) !== null)
        .sort((a, b) => a.z - b.z || a.createdAt - b.createdAt);
      const byId = new Map(shapes.map((s) => [s.id, s] as const));
      let counter = 1;
      for (const s of others) {
        while (counter === value) counter++; // не занимаем слот целевого shape
        byId.set(s.id, withNumber(s, counter));
        counter++;
      }
      shapes = shapes.map((s) => byId.get(s.id) ?? s);
    }
    this.project.shapes = shapes;
    this.touch();
  }

  /** Принудительная перенумерация всех маркеров по z-порядку. */
  renumberAll(): void {
    this.commit();
    this.project.shapes = renumberAll(this.project.shapes);
    this.touch();
  }

  /** Проверка коллизии номера (для UI-предупреждения). */
  hasNumberCollision(id: string, value: number): boolean {
    return findNumberCollision(this.project.shapes, id, value) !== null;
  }
}

// Единственный глобальный инстанс
export const editor = new EditorStore();
